 import { motion } from "framer-motion";
 import { Book, BookOpen, ChevronRight, Heart, Sparkles, Sun } from "lucide-react";
 import { getDayDua, getPeriodForDay } from "@/data/dailyDuas";
 import { getDayHadith } from "@/data/dailyHadiths";
 import { useFavourites } from "@/hooks/useFavourites";

interface TodaysFocusProps {
  currentDay: number;
  onViewDua: () => void;
}

export const TodaysFocus = ({ currentDay, onViewDua }: TodaysFocusProps) => {
  const dua = getDayDua(currentDay);
  const period = getPeriodForDay(currentDay);
   const hadith = getDayHadith(currentDay);
  const { isFavourite, toggleFavourite } = useFavourites();

  if (!dua || !period) return null;

  // Get affirmation for the day (cycle through period affirmations)
  const affirmationIndex = (currentDay - 1) % period.affirmations.length;
  const todaysAffirmation = period.affirmations[affirmationIndex];

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'prophetic': return 'Prophetic Dua';
      case 'quranic': return 'Quranic Dua';
      case 'recommended': return 'Recommended Dua';
      default: return 'Dua';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
      className="bg-card border border-border/50 rounded-xl overflow-hidden"
    >
      {/* Header with gradient */}
      <div className="bg-gradient-to-br from-primary/10 to-accent/20 px-6 py-4 border-b border-border/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="font-display text-lg text-foreground">Today's Focus</h2>
              <p className="text-xs text-muted-foreground">Day {currentDay}</p>
            </div>
          </div>
          <button
            onClick={() => toggleFavourite('dua', dua.day.toString())}
            className="p-2 hover:bg-background/50 rounded-full transition-colors"
          >
            <Heart 
              className={`w-5 h-5 ${
                isFavourite('dua', dua.day.toString()) 
                  ? 'fill-destructive text-destructive' 
                  : 'text-muted-foreground'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Today's Affirmation */}
       {/* Hadith of the Day */}
       {hadith && (
         <div className="px-6 py-5 border-b border-border/30">
           <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
             <Book className="w-3.5 h-3.5" />
             <span className="uppercase tracking-wider">Hadith of the Day</span>
           </div>
 
           {/* Arabic */}
           <p 
             className="font-arabic text-xl text-foreground leading-loose text-center mb-3" 
             dir="rtl"
           >
             {hadith.arabic}
           </p>
 
           {/* Transliteration */}
           <p className="text-sm italic text-muted-foreground text-center mb-3">
             {hadith.transliteration}
           </p>
 
           {/* Translation */}
           <blockquote className="text-center text-foreground/90 leading-relaxed border-l-2 border-accent/50 pl-4 py-2 bg-accent/10 rounded-r-md mb-2">
             "{hadith.translation}"
           </blockquote>
 
           <p className="text-xs text-muted-foreground text-center uppercase tracking-wider">
             — {hadith.source}
           </p>
         </div>
       )}
 
      {/* Dua Content */}
      <div className="p-6 space-y-4">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <BookOpen className="w-3.5 h-3.5" />
          <span className="uppercase tracking-wider">{getTypeLabel(dua.type)}</span>
        </div>

        {/* Arabic */}
        <p 
           className="font-arabic text-2xl text-foreground leading-loose text-center" 
          dir="rtl"
        >
          {dua.arabic}
        </p>

        {/* Transliteration */}
        <p className="text-sm italic text-muted-foreground text-center">
          {dua.transliteration}
        </p>

        {/* Translation */}
        <blockquote className="text-center text-foreground/90 leading-relaxed border-l-2 border-primary/30 pl-4 py-2 bg-muted/30 rounded-r-md">
          "{dua.translation}"
        </blockquote>

        <p className="text-xs text-muted-foreground text-center uppercase tracking-wider">
          — {dua.source}
        </p>

         {/* Today's Affirmation */}
         <div className="pt-4 mt-4 border-t border-border/30">
           <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
             <Sun className="w-3.5 h-3.5" />
             <span className="uppercase tracking-wider">Affirmation</span>
           </div>
           <p className="text-foreground italic leading-relaxed text-center">
             "{todaysAffirmation}"
           </p>
         </div>
 
        {/* View Full Page Button */}
        <button
          onClick={onViewDua}
          className="w-full mt-2 py-3 px-4 bg-primary/10 hover:bg-primary/20 text-primary rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors"
        >
          View Full Daily Page
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );
};
